import React, {useEffect, useRef, useState} from "react";
import {isExpandedSentimentTypesEnabled} from "../../../shared/access";
import {
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    TextField,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    ListItemText,
    Tooltip
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {toast} from "react-toastify";
import {API} from "aws-amplify";
import { makeStyles } from '@mui/styles';
import IconButton from '@mui/material/IconButton';

const useStyles = makeStyles(() => ({
    selectIcon: {
        color: "var(--colorTitle)", // your custom color
    },
}));
function CreateEditPublicFeedbackBoard(props) {
    const isExpandedSentiment = isExpandedSentimentTypesEnabled();

    const FEEDBACKS = {
        'POSITIVE': 'Positive',
        'MIXED': 'Mixed',
        'NEGATIVE': 'Negative',
        'NEUTRAL': 'Neutral',
        ...(isExpandedSentiment && {
            'NONE': 'None',
            'OTHER': 'Other',
        }),
    };

    const PERSONNEL_TYPES = {
        'CALLTAKER': 'Calltaker',
        'RESPONDER': 'Responder',
        //  'INVESTIGATOR': 'Investigator',
        'ALL': 'All',
        'NONE': 'None'
    };

    const [selectedFeedbacks, setSelectedFeedbacks] = useState(
        Object.fromEntries(Object.keys(FEEDBACKS).map(k => [k, k === 'POSITIVE']))
    );
    const classes = useStyles();

    const [selectedPersonnelType, setSelectedPersonnelType] = useState(Object.fromEntries(Object.keys(PERSONNEL_TYPES).map(f => [f, true])));
    const [feedbackName, setFeedbackName] = useState('');
    const [viewType, setViewType] = useState('LIST');
    const [showPersonnelName, setShowPersonnelName] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentSetting, setCurrentSetting] = useState(null);
    const [refreshRate, setRefreshRate] = useState("48");

    const [selectedFeedbackRange, setSelectedFeedbackRange] = useState('DAYS');
    const [lastDays, setLastDays] = useState("7");
    const [lastPieces, setLastPieces] = useState('');

    const ALL_SURVEYS_ID = "ALL";
    const ALL_SURVEYS_LABEL = "All Surveys";
    const [surveyOptions, setSurveyOptions] = useState([]);
    const [surveyLoading, setSurveyLoading] = useState(false);
    const [includedSurveys, setIncludedSurveys] = useState([ALL_SURVEYS_ID]);
    const selectRef = useRef(null);
    const lastSurveysRef = useRef(includedSurveys);
    const feedbackNameRef = useRef(null);
    const refreshRateRef = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [announceText, setAnnounceText] = useState('');
    const [hasDropdownBeenOpened, setHasDropdownBeenOpened] = useState(false);
    const announcerRef = useRef(null);
    const isFeedbackVersionV2 = window.__RUNTIME_CONFIG__?.FEEDBACK_VERSION === 'v2';
    const [hasValidatedSurveys, setHasValidatedSurveys] = useState(false);
    const [noteworthy, setNoteworthy] = useState(false);

    const [fieldErrors, setFieldErrors] = useState({
        feedbackName: '',
        refreshRate: '',
        personnelType: '',
        feedbackType: '',
        surveys: '',
        feedbackRange: '',
    });

    const setFieldError = (field, message) =>
        setFieldErrors(prev => ({ ...prev, [field]: message }));
    const clearFieldError = field =>
        setFieldErrors(prev => ({ ...prev, [field]: '' }));

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = '.Toastify__close-button:focus-visible { outline: 3px solid #005fcc; outline-offset: 2px; border-radius: 2px; }';
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    useEffect(() => {
        if (announcerRef.current && document.body.contains(announcerRef.current)) {
            document.body.removeChild(announcerRef.current);
            announcerRef.current = null;
        }
        const message = isDropdownOpen
            ? 'Included Surveys dropdown expanded'
            : isDropdownOpen === false && hasDropdownBeenOpened
                ? 'Included Surveys dropdown collapsed'
                : '';
        if (message) {
            const announcer = document.createElement('div');
            announcer.setAttribute('role', 'status');
            announcer.setAttribute('aria-live', 'assertive');
            announcer.setAttribute('aria-atomic', 'true');
            announcer.style.cssText = `
            position: absolute;
            left: -10000px;
            width: 1px;
            height: 1px;
            overflow: hidden;`;
            document.body.appendChild(announcer);
            announcerRef.current = announcer;
            setTimeout(() => {
                if (announcer && document.body.contains(announcer)) {
                    announcer.textContent = message;
                }
            }, 5);
        }
        return () => {
            if (announcerRef.current && document.body.contains(announcerRef.current)) {
                document.body.removeChild(announcerRef.current);
                announcerRef.current = null;
            }
        };
    }, [isDropdownOpen, hasDropdownBeenOpened]);

    useEffect(() => {
        if (!isFeedbackVersionV2) return;
        setSurveyLoading(true);
        API.get("Core", "/api/v1/survey/summary")
            .then((res) => {
                const options = Array.isArray(res) ? res.map((s) => ({ id: String(s.id), name: s.name })) : [];
                options.unshift({ id: ALL_SURVEYS_ID, name: ALL_SURVEYS_LABEL });
                setSurveyOptions(options);
            })
            .catch((err) => {
                console.error("Failed to load surveys", err);
                toast.error("Failed to load surveys", { position: "top-center" });
            })
            .finally(() => setSurveyLoading(false));
    }, []);

    const createFeedbackSettingData = () => ({
        ...currentSetting,
        name: feedbackName,
        viewType: viewType,
        showPersonnelName: viewType === 'FOCUS' ? showPersonnelName : true,
        personnelType: Object.keys(selectedPersonnelType).filter(f => selectedPersonnelType[f]).join(','),
        feedbackType: Object.keys(selectedFeedbacks).filter(f => selectedFeedbacks[f]).join(','),
        refreshRate: parseInt(refreshRate, 10),
        noteworthy: isExpandedSentiment ? noteworthy : false,
        ...(isFeedbackVersionV2 && {
            lastDays: lastDays ? parseInt(lastDays, 10) : null,
            lastPieces: lastPieces ? parseInt(lastPieces, 10) : null,
            surveyIds: includedSurveys.includes(ALL_SURVEYS_ID)
                ? "ALL"
                : includedSurveys.join(","),
        }),
    });

    const updateFeedback = feedback => {
        const newSelectedFeedbacks = {...selectedFeedbacks};
        newSelectedFeedbacks[feedback] = !newSelectedFeedbacks[feedback];
        setSelectedFeedbacks(newSelectedFeedbacks);
    }

    const updatePersonnelType = personnelType => {
        const newSelectedPersonnelType = {...selectedPersonnelType};
        newSelectedPersonnelType[personnelType] = !newSelectedPersonnelType[personnelType];
        setSelectedPersonnelType(newSelectedPersonnelType);
    }

    const onCancel = () => {
        props.history.goBack();
    }

    const handleFeedbackRangeChange = (type) => {
        setSelectedFeedbackRange(type);
        if (type === 'DAYS') setLastPieces('');
        else setLastDays('');
    };

    const handleLastDaysChange = (e) => setLastDays(e.target.value);
    const handleLastPiecesChange = (e) => setLastPieces(e.target.value);

    const isAnyPersonnelTypeSelected = () => {
        return Object.values(selectedPersonnelType).some(personnel => personnel);
    };
    const isAnyFeedbackTypeSelected = () => {
        return Object.values(selectedFeedbacks).some(feedback => feedback);
    };

    const handleSurveyChange = (event) => {
        const value = event.target.value;
        const newVals = value.map((v) => String(v));
        const currentlyHasAll = includedSurveys.includes(ALL_SURVEYS_ID);
        const selectingAll = newVals.includes(ALL_SURVEYS_ID);
        const selectingOthers = newVals.some((v) => v !== ALL_SURVEYS_ID);
        let finalVals;
        if (currentlyHasAll && selectingOthers) {
            finalVals = newVals.filter((v) => v !== ALL_SURVEYS_ID);
        } else if (!currentlyHasAll && selectingAll) {
            finalVals = [ALL_SURVEYS_ID];
        } else {
            finalVals = newVals;
        }
        lastSurveysRef.current = finalVals;
        setIncludedSurveys(finalVals);
        if (finalVals.length > 0) clearFieldError('surveys');
    };



    const focusFirstErrorField = (errors) => {
        if (errors.feedbackName) {
            feedbackNameRef.current?.focus();
        } else if (errors.surveys) {
            document.getElementById('included-surveys-select')?.focus();
        } else if (errors.personnelType) {
            document.querySelector('[aria-labelledby~="personnelTypesLabel"] input[type="checkbox"]')?.focus();
        } else if (errors.feedbackType) {
            document.querySelector('[aria-labelledby~="feedbackTypesLabel"] input[type="checkbox"]')?.focus();
        } else if (errors.feedbackRange) {
            const label = errors.feedbackRange.includes('Last days')
                ? 'Enter number of days(1-30) for feedback range'
                : 'Enter number of feedback pieces(1-250) for Feedback range';
            document.querySelector(`[aria-label="${label}"]`)?.focus();
        } else if (errors.refreshRate) {
            refreshRateRef.current?.focus();
        }
    };

    const saveFeedbackSetting = () => {
        const newErrors = {
            feedbackName: '',
            refreshRate: '',
            personnelType: '',
            feedbackType: '',
            surveys: '',
            feedbackRange: '',
        };

        if (!feedbackName || feedbackName === '') newErrors.feedbackName = 'Feedback Name is required';
        if (!isAnyPersonnelTypeSelected()) newErrors.personnelType = 'At least one Personnel Type must be selected';
        if (!isAnyFeedbackTypeSelected()) newErrors.feedbackType = 'At least one Feedback Type must be selected';

        const numericRefreshValue = parseInt(refreshRate, 10);
        if (
            refreshRate === '' ||
            !/^\d*$/.test(refreshRate) ||
            isNaN(numericRefreshValue) ||
            !Number.isInteger(numericRefreshValue) ||
            numericRefreshValue < 1 ||
            numericRefreshValue > 168
        ) newErrors.refreshRate = 'Refresh Rate must be a whole number between 1 and 168.';

        if (isFeedbackVersionV2) {
            const numericLastDays = parseInt(lastDays, 10);
            const numericLastPieces = parseInt(lastPieces, 10);
            if (!includedSurveys || includedSurveys.length === 0) {
                newErrors.surveys = 'At least one survey option must be selected in Included Surveys.';
            }
            if (selectedFeedbackRange === 'DAYS' &&
                (lastDays === '' ||
                    !/^\d*$/.test(lastDays) ||
                    isNaN(numericLastDays) ||
                    !Number.isInteger(numericLastDays) ||
                    numericLastDays < 1 ||
                    numericLastDays > 30)
            ) newErrors.feedbackRange = 'The value entered for Last days in feedback range must be a whole number between 1 and 30.';
            if (selectedFeedbackRange === 'PIECES' &&
                (lastPieces === '' ||
                    !/^\d*$/.test(lastPieces) ||
                    isNaN(numericLastPieces) ||
                    !Number.isInteger(numericLastPieces) ||
                    numericLastPieces < 1 ||
                    numericLastPieces > 250)
            ) newErrors.feedbackRange = 'The value entered for Last Pieces of Feedback in feedback range must be a whole number between 1 and 250.';
        }

        setFieldErrors(newErrors);
        const firstError = Object.values(newErrors).find(e => e);
        if (firstError) {
            toast.error('Please fill in all required fields before saving.', {
                position: "top-center",
                onOpen: () => {
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            const closeBtns = document.querySelectorAll('.Toastify__close-button');
                            if (closeBtns.length > 0) {
                                const closeBtn = closeBtns[closeBtns.length - 1];
                                closeBtn.focus();
                                const handleTabKey = (e) => {
                                    if (e.key === 'Tab') {
                                        e.preventDefault();
                                        closeBtn.removeEventListener('keydown', handleTabKey);
                                        closeBtn.click();
                                    }
                                };
                                closeBtn.addEventListener('keydown', handleTabKey);
                            }
                        });
                    });
                },
                onClose: () => focusFirstErrorField(newErrors),
            });
            return;
        }

        const DATA = createFeedbackSettingData();
        setSaving(true);
        let savingRequest;

        if (currentSetting?.id) {
            savingRequest = API.put('Core', `/api/v1/public-feedback-board/${currentSetting.id}`, {body: DATA})
                .then(r => {
                    const SETTING = r;

                    if (SETTING) {
                        setCurrentSetting(SETTING);
                        toast.success("Feedback Setting Updated!", {position: "top-center"});
                    }
                })
        } else {

            savingRequest = API.post('Core', '/api/v1/public-feedback-board', {body: DATA})
                .then(r => {
                    const SETTING = r;

                    if (SETTING) {
                        setCurrentSetting(SETTING);
                        toast.success("Feedback Setting Created!", {position: "top-center"});
                    }
                })
        }

        savingRequest.finally(() => setSaving(false));
    }
    useEffect(() => {

        const ID = props?.match?.params?.id;
        if (!ID) return;

        setLoading(true);
        API.get('Core', `/api/v1/public-feedback-board/${ID}`)
            .then(r => {
                setCurrentSetting(r || {});
                if (r) {

                    const SELECTED_FEEDBACK = Object.keys(PERSONNEL_TYPES).reduce((acc, key) => {
                        acc[key] = false;
                        return acc;
                    }, {});

                    const SELECTED_PERSONNEL_TYPE = Object.keys(PERSONNEL_TYPES).reduce((acc, key) => {
                        acc[key] = false;
                        return acc;
                    }, {});


                    // Debugging: Log the data being set
                    console.log("Setting feedbackName:", r.name);
                    console.log("Setting viewType:", r.viewType);
                    console.log("Setting showPersonnelName:", r.showPersonnelName);
                    console.log("Selected Feedbacks:", r.feedbackType.split(','));
                    console.log("Selected Personnel Types:", r.personnelType.split(','));
                    console.log("Setting refresh rate:", r.refreshRate);

                    setFeedbackName(r.name);
                    setViewType(r.viewType);
                    setShowPersonnelName(r.showPersonnelName);
                    setRefreshRate(String(r.refreshRate));
                    if (isExpandedSentiment) setNoteworthy(!!r.noteworthy);

                    r.feedbackType.split(',').forEach(feedback => {
                        if (FEEDBACKS[feedback]) {
                            SELECTED_FEEDBACK[feedback] = true;
                        }
                    });
                    setSelectedFeedbacks(SELECTED_FEEDBACK);

                    r.personnelType.split(',').forEach(personnel => {
                        if (PERSONNEL_TYPES[personnel]) {
                            SELECTED_PERSONNEL_TYPE[personnel] = true;
                        }
                    });
                    setSelectedPersonnelType(SELECTED_PERSONNEL_TYPE);

                    if (isFeedbackVersionV2) {
                        console.log("Setting last days:", r.lastDays);
                        console.log("Setting last pieces:", r.lastPieces);
                        console.log("Setting surveyIds:", r.surveyIds);
                        if (r.lastDays != null) setSelectedFeedbackRange('DAYS');
                        else if (r.lastPieces != null) setSelectedFeedbackRange('PIECES');

                        setLastDays(r.lastDays != null ? String(r.lastDays) : '');
                        setLastPieces(r.lastPieces != null ? String(r.lastPieces) : '') ;
                        if (r.surveyIds) {
                            if (String(r.surveyIds).toUpperCase() === "ALL") {
                                setIncludedSurveys([ALL_SURVEYS_ID]);
                            } else {
                                const parsed = String(r.surveyIds)
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                    .map((s) => String(s));
                                if (parsed.length > 0) setIncludedSurveys(parsed);
                                else setIncludedSurveys([ALL_SURVEYS_ID]);
                            }
                        } else {
                            setIncludedSurveys([ALL_SURVEYS_ID]);
                        }
                    }
                }
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!isFeedbackVersionV2 || surveyLoading || surveyOptions.length === 0) return;
        if (!currentSetting) return;
        if (hasValidatedSurveys) return;
        if (includedSurveys.includes(ALL_SURVEYS_ID)) {
            setHasValidatedSurveys(true);
            return;
        }
        const validSurveyIds = surveyOptions
            .filter(opt => opt.id !== ALL_SURVEYS_ID)
            .map(opt => String(opt.id));
        const validatedSurveys = includedSurveys.filter(id =>
            validSurveyIds.includes(String(id))
        );
        if (validatedSurveys.length === 0) {
            setIncludedSurveys([ALL_SURVEYS_ID]);
            setHasValidatedSurveys(true);
        } else if (validatedSurveys.length !== includedSurveys.length) {
            setIncludedSurveys(validatedSurveys);
            setHasValidatedSurveys(true);
        } else {
            setHasValidatedSurveys(true);
        }

    }, [surveyOptions, surveyLoading, hasValidatedSurveys, includedSurveys, currentSetting]);

    const validateFeedbackName = (value) => {
        if (!value || value === '') {
            setFieldError('feedbackName', 'Feedback Name is required');
            return false;
        }
        clearFieldError('feedbackName');
        return true;
    };

    const validatePersonnelType = (types) => {
        if (!Object.values(types).some(v => v)) {
            setFieldError('personnelType', 'At least one Personnel Type must be selected');
            return false;
        }
        clearFieldError('personnelType');
        return true;
    };

    const validateFeedbackType = (types) => {
        if (!Object.values(types).some(v => v)) {
            setFieldError('feedbackType', 'At least one Feedback Type must be selected');
            return false;
        }
        clearFieldError('feedbackType');
        return true;
    };

    const validateSurveys = (surveys) => {
        if (!surveys || surveys.length === 0) {
            setFieldError('surveys', 'At least one survey option must be selected in Included Surveys.');
            return false;
        }
        clearFieldError('surveys');
        return true;
    };

    const validateFeedbackRange = (range, days, pieces) => {
        const numericDays = parseInt(days, 10);
        const numericPieces = parseInt(pieces, 10);
        if (range === 'DAYS' &&
            (days === '' || !/^\d*$/.test(days) || isNaN(numericDays) || !Number.isInteger(numericDays) || numericDays < 1 || numericDays > 30)
        ) {
            setFieldError('feedbackRange', 'The value entered for Last days in feedback range must be a whole number between 1 and 30.');
            return false;
        }
        if (range === 'PIECES' &&
            (pieces === '' || !/^\d*$/.test(pieces) || isNaN(numericPieces) || !Number.isInteger(numericPieces) || numericPieces < 1 || numericPieces > 250)
        ) {
            setFieldError('feedbackRange', 'The value entered for Last Pieces of Feedback in feedback range must be a whole number between 1 and 250.');
            return false;
        }
        clearFieldError('feedbackRange');
        return true;
    };

    const validateRefreshRate = (value) => {
        const numeric = parseInt(value, 10);
        if (
            value === '' ||
            !/^\d*$/.test(value) ||
            isNaN(numeric) ||
            !Number.isInteger(numeric) ||
            numeric < 1 ||
            numeric > 168
        ) {
            setFieldError('refreshRate', 'Refresh Rate must be a whole number between 1 and 168.');
            return false;
        }
        clearFieldError('refreshRate');
        return true;
    };

    const handleViewTypeChange = (event) => {
        setViewType(event.target.value);
        if (event.target.value === 'LIST') {
            setShowPersonnelName(true);
        }
    };

    const handleShowPersonnelNameChange = (event) => {
        setShowPersonnelName(event.target.value === 'true');
    };

    const handleClearSelection = () => {
        lastSurveysRef.current = [];
        setIncludedSurveys([]);
        setFieldError('surveys', 'At least one survey option must be selected in Included Surveys.');
        setAnnounceText('Survey selection cleared.');
        if (selectRef.current) {
            const dropdownElement = selectRef.current.querySelector('#included-surveys-select');
            if (dropdownElement) {
                dropdownElement.focus();
            }
        }
    };

    return (
        <div className='publicFeedbackView'>
            <div className="navBar navBarFeedback">
                <h1 className="title navBarFeedbackTitle">Public Feedback Board Settings</h1>
            </div>
            <div style={{marginTop: '10px'}} className="inputText">Fields marked with <span
                style={{color: 'red'}}>*</span> are required.
            </div>
            {!loading ? <>
                <section>
                    <div className='publicFeedbackNameHeader' id="feedbackBoardNameLabel">Public Feedback Board Name <span style={{ color: 'red' }} aria-hidden="true">*</span></div>
                    <div className='publicFeedbackNameContainer'>
                        <TextField value={feedbackName}
                                   onChange={event => { setFeedbackName(event.target.value); if (fieldErrors.feedbackName) validateFeedbackName(event.target.value); }}
                                   onBlur={event => validateFeedbackName(event.target.value)}
                                   variant="outlined"
                                   className="publicFeedbackNameInput"
                                   inputRef={feedbackNameRef}
                                   error={!!fieldErrors.feedbackName}
                                   helperText={fieldErrors.feedbackName || ''}
                                   inputProps={{
                                       'aria-labelledby': 'feedbackBoardNameLabel',
                                       'aria-required': 'true',
                                       'aria-describedby': fieldErrors.feedbackName ? 'feedbackNameError' : undefined,
                                   }}
                                   FormHelperTextProps={{ id: 'feedbackNameError', 'aria-live': 'polite' }}
                        />
                        <div>This is only used for Settings purposes</div>
                    </div>
                </section>
                <section>
                    <div className='publicFeedbackHeader' id="viewTypeLabel">View Type</div>
                    <div className='publicFeedbackContainer' role="radiogroup" aria-labelledby="viewTypeLabel">
                        <div className='publicFeedbackOption'>
                            <label>
                                <input
                                    type="radio"
                                    value="LIST"
                                    checked={viewType === 'LIST'}
                                    onChange={handleViewTypeChange}
                                    aria-setsize={2}
                                    aria-posinset={1}
                                />List
                            </label>
                        </div>
                        <div className='publicFeedbackOption'>
                            <label>
                                <input
                                    type="radio"
                                    value="FOCUS"
                                    checked={viewType === 'FOCUS'}
                                    onChange={handleViewTypeChange}
                                    aria-setsize={2}
                                    aria-posinset={2}
                                /> Single entry (focus)
                            </label>
                        </div>
                    </div>
                </section>
                <section hidden={viewType === 'LIST'}>
                    <div className='publicFeedbackHeader'>Show Personnel Name</div>
                    <div className='publicFeedbackContainer'>
                        <div className='publicFeedbackOption'>
                            <label>
                                <input
                                    type="radio"
                                    name="showPersonnelName"
                                    value='true'
                                    checked={showPersonnelName === true}
                                    onChange={handleShowPersonnelNameChange}
                                /> On
                            </label>
                        </div>
                        <div className='publicFeedbackOption'>
                            <label>
                                <input
                                    type="radio"
                                    name="showPersonnelName"
                                    value='false'
                                    checked={showPersonnelName === false}
                                    onChange={handleShowPersonnelNameChange}
                                /> Off
                            </label>
                        </div>
                    </div>
                </section>
                {isFeedbackVersionV2 && (
                <section>
                    <div className="publicFeedbackHeader">
                        Included Surveys <span style={{ color: "red" }}>*</span>
                    </div>

                    <div className="publicFeedbackContainer" style={{ position: "relative" }}>
                        <FormControl
                            variant="outlined"
                            style={{ minWidth: 300, position: "relative" }}
                        >
                            <InputLabel shrink id="included-surveys-label" style={{ color: "var(--colorTitle)" }}>
                                Included Surveys
                                <span style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>required</span>
                            </InputLabel>
                            <div style={{ position: "relative", width: "100%" }}>
                                <Select
                                    labelId="included-surveys-label"
                                    id="included-surveys-select"
                                    multiple
                                    value={includedSurveys}
                                    onChange={handleSurveyChange}
                                    label="Included Surveys"
                                    ref={selectRef}
                                    displayEmpty
                                    open={isDropdownOpen}
                                    onOpen={() => {
                                        setIsDropdownOpen(true);
                                        setHasDropdownBeenOpened(true);
                                    }}
                                    onClose={() => {
                                        setIsDropdownOpen(false);
                                        if (hasDropdownBeenOpened) validateSurveys(lastSurveysRef.current);
                                    }}
                                    style={{
                                        width: "100%",
                                        cursor: "pointer",
                                        backgroundColor: "var(--colorSubtitleSub)",
                                        color: "var(--colorTitle)",
                                    }}
                                    classes={{ icon: classes.selectIcon }}
                                    renderValue={(selected) => {
                                        if (surveyLoading) {
                                            return (
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#888" }}>
                                                    <CircularProgress size={16} />
                                                    <span>Loading surveys...</span>
                                                </div>
                                            );
                                        }
                                        const total = surveyOptions.length;
                                        if (!selected || selected.length === 0) {
                                            return <span>0/{total} Surveys selected</span>;
                                        }

                                        const allSelected = selected.includes(String(ALL_SURVEYS_ID));
                                        const selectedCount = allSelected ? total : selected.length;

                                        return (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    height: "100%",
                                                }}
                                            >
                                                {selectedCount}/{total} Surveys selected
                                            </div>
                                        );
                                    }}
                                    inputProps={{
                                        "aria-describedby": fieldErrors.surveys ? "included-surveys-help surveysError" : "included-surveys-help",
                                        "aria-expanded": isDropdownOpen,
                                        "aria-haspopup": "listbox",
                                        "role": "combobox"
                                    }}
                                    SelectDisplayProps={{
                                        "aria-required": "true"
                                    }}
                                    MenuProps={{
                                        MenuListProps: {
                                            "aria-label": "Survey selection options",
                                            "role": "listbox"
                                        },
                                        getContentAnchorEl: null,
                                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                        transformOrigin: { vertical: "top", horizontal: "left" },
                                        PaperProps: {
                                            style: {
                                                maxHeight: 200,
                                                overflowY: "auto",
                                                overflowX: "auto",
                                                width: selectRef.current
                                                    ? selectRef.current.clientWidth
                                                    : 300,
                                            },
                                        },
                                    }}
                                >
                                    {surveyLoading ? (
                                        <MenuItem disabled>
                                            <em>Loading...</em>
                                        </MenuItem>
                                    ) : (
                                        surveyOptions.map((opt) => {
                                            const checked = includedSurveys.includes(String(opt.id));
                                            return (
                                                <MenuItem
                                                    key={`survey-option-${opt.id}`}
                                                    value={String(opt.id)}
                                                    style={{
                                                        backgroundColor: checked ? "var(--colorTint)" : "transparent",
                                                        color: checked ? "var(--colorTitle)" : "inherit",
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        style={{
                                                            color: checked ? "#D5ACED" : "#999",
                                                        }}
                                                    />
                                                    <ListItemText
                                                        primary={opt.name}
                                                        primaryTypographyProps={{
                                                            style: {
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                            },
                                                        }}
                                                    />
                                                </MenuItem>
                                            );
                                        })
                                    )}
                                </Select>
                                <div aria-live="polite" style={{ position: "absolute", left: "-9999px" }}>
                                    {hasDropdownBeenOpened ? `${includedSurveys.length}/${surveyOptions.length} Surveys selected` : ''}
                                </div>
                                {includedSurveys.length > 0 && (
                                    <IconButton
                                        size="small"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }}
                                        onClick={() => {
                                            handleClearSelection();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                handleClearSelection();
                                            }
                                        }}
                                        style={{
                                            position: "absolute",
                                            right: 35,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            zIndex: 2,
                                            padding: 4,
                                            color: "var(--colorTitle)",
                                        }}
                                        aria-label="Clear selection"
                                    >
                                        ✕
                                    </IconButton>
                                )}
                            </div>

                            <div
                                id="included-surveys-help"
                                style={{ marginTop: 6 }}
                                className="inputText"
                            >
                                Select one or more surveys to include. Select{" "}
                                <strong>All Surveys</strong> to include all surveys.
                            </div>
                            <div id="surveysError" aria-live="polite" style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                                {fieldErrors.surveys}
                            </div>
                            <div
                                aria-live="assertive"
                                aria-atomic="true"
                                style={{
                                    position: "absolute",
                                    left: "-10000px",
                                    width: "1px",
                                    height: "1px",
                                    overflow: "hidden",
                                    clip: "rect(0 0 0 0)"
                                }}
                            >
                                {announceText}
                            </div>
                        </FormControl>
                    </div>
                </section>
                )}
                <section>
                    <div className='publicFeedbackHeader' id="personnelTypesLabel">Personnel Types <span style={{ color: 'red' }} aria-hidden="true">*</span></div>
                    <div className='publicFeedbackContainer'>
                        <span id="personnelTypesRequired" style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>required</span>
                        <FormGroup className='publicFeedbackPersonnelType' row role="group" aria-labelledby="personnelTypesLabel personnelTypesRequired" aria-describedby={fieldErrors.personnelType ? 'personnelTypeError' : undefined}>
                            {Object.keys(PERSONNEL_TYPES).map((key, i) => (
                                <FormControlLabel
                                    className='publicFeedbackPersonnelTypeOption'
                                    key={`emailDigestSubscriptionPersonnelOption${i}`}
                                    label={PERSONNEL_TYPES[key]}
                                    control={<Checkbox checked={selectedPersonnelType[key]} inputProps={{'aria-label': PERSONNEL_TYPES[key]}}/>}
                                    onClick={() => { const next = { ...selectedPersonnelType, [key]: !selectedPersonnelType[key] }; setSelectedPersonnelType(next); validatePersonnelType(next); }}
                                />
                            ))}
                        </FormGroup>
                        <div id="personnelTypeError" aria-live="polite" style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                            {fieldErrors.personnelType}
                        </div>
                    </div>
                </section>
                <section>
                    <div className='publicFeedbackHeader' id="feedbackTypesLabel">Types of Feedback <span style={{ color: 'red' }} aria-hidden="true">*</span></div>
                    <div className='publicFeedbackContainer'>
                        <span id="feedbackTypesRequired" style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>required</span>
                        <FormGroup className='publicFeedbackPersonnelType' row role="group" aria-labelledby="feedbackTypesLabel feedbackTypesRequired" aria-describedby={fieldErrors.feedbackType ? 'feedbackTypeError' : undefined}>
                            {Object.keys(FEEDBACKS).map((key, i) => (
                                <FormControlLabel
                                    className='publicFeedbackPersonnelTypeOption'
                                    key={`emailDigestSubscriptionFeedbackOption${i}`}
                                    label={FEEDBACKS[key]}
                                    control={<Checkbox checked={selectedFeedbacks[key]} inputProps={{'aria-label': FEEDBACKS[key]}}/>}
                                    onClick={() => { const next = { ...selectedFeedbacks, [key]: !selectedFeedbacks[key] }; setSelectedFeedbacks(next); validateFeedbackType(next); }}
                                />
                            ))}
                        </FormGroup>
                        <div id="feedbackTypeError" aria-live="polite" style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                            {fieldErrors.feedbackType}
                        </div>
                        {isExpandedSentiment && (
                            <FormGroup style={{ marginTop: '8px' }}>
                                <FormControlLabel
                                    label={
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            Include only Noteworthy Feedback
                                            <Tooltip
                                                title="When the Noteworthy Feedback checkbox is selected, the chosen sentiment(s) will trigger the actions only if the Feedback is also marked as Noteworthy. If the Noteworthy Feedback checkbox is not selected, all Feedback matching the chosen sentiment(s) will trigger the actions."
                                                arrow
                                            >
                                                <span
                                                    tabIndex={0}
                                                    role="button"
                                                    aria-roledescription="tooltip"
                                                    aria-label="When the Noteworthy Feedback checkbox is selected, the chosen sentiment(s) will trigger the actions only if the Feedback is also marked as Noteworthy. If the Noteworthy Feedback checkbox is not selected, all Feedback matching the chosen sentiment(s) will trigger the actions."
                                                    style={{ display: 'inline-flex', cursor: 'pointer' }}
                                                >
                                                    <InfoOutlinedIcon
                                                        fontSize="small"
                                                        aria-hidden="true"
                                                        sx={{ verticalAlign: 'middle', color: 'text.secondary' }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </span>
                                    }
                                    control={
                                        <Checkbox
                                            checked={noteworthy}
                                            onChange={(e) => setNoteworthy(e.target.checked)}
                                            inputProps={{'aria-label': 'Include only Noteworthy Feedback'}}
                                        />
                                    }
                                />
                            </FormGroup>
                        )}
                    </div>
                </section>
                {isFeedbackVersionV2 && (
                <section>
                    <div className='publicFeedbackHeader' id="feedbackRangeLabel">
                        Feedback Range <span style={{ color: 'red' }} aria-hidden="true">*</span>
                    </div>
                    <span id="feedbackRangeRequired" style={{ position: 'absolute', width: '1px', height: '1px', margin: '-1px', padding: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>required</span>
                    <div
                        className='publicFeedbackContainer'
                        role="radiogroup"
                        aria-labelledby="feedbackRangeLabel feedbackRangeRequired"
                        aria-describedby={fieldErrors.feedbackRange ? 'feedbackRangeError' : undefined}
                    >
                        <div className='publicFeedbackOption'>
                            <label className="inputText">
                                <input
                                    type="radio"
                                    value="DAYS"
                                    checked={selectedFeedbackRange === 'DAYS'}
                                    onChange={() => { handleFeedbackRangeChange('DAYS'); clearFieldError('feedbackRange'); }}
                                    aria-checked={selectedFeedbackRange === 'DAYS'}
                                    aria-label="Select Last Days(1-30) for feedback range"
                                    aria-setsize={2}
                                    aria-posinset={1}
                                />
                                Last{' '}
                                <input
                                    style={{ backgroundColor: 'var(--colorSubtitleSub)' }}
                                    type="text"
                                    value={lastDays}
                                    onChange={e => { handleLastDaysChange(e); if (fieldErrors.feedbackRange) clearFieldError('feedbackRange'); }}
                                    onBlur={() => { if (selectedFeedbackRange === 'DAYS') validateFeedbackRange('DAYS', lastDays, lastPieces); }}
                                    disabled={selectedFeedbackRange !== 'DAYS'}
                                    aria-label="Enter number of days(1-30) for feedback range"
                                    className="feedbackRangeInput"
                                />{' '}
                                Days (1-30)
                            </label>
                        </div>
                        <div className='publicFeedbackOption'>
                            <label className="inputText">
                                <input
                                    type="radio"
                                    value="PIECES"
                                    checked={selectedFeedbackRange === 'PIECES'}
                                    onChange={() => { handleFeedbackRangeChange('PIECES'); clearFieldError('feedbackRange'); }}
                                    aria-checked={selectedFeedbackRange === 'PIECES'}
                                    aria-label="Select Last Pieces(1-250) for Feedback range"
                                    aria-setsize={2}
                                    aria-posinset={2}
                                />
                                Last{' '}
                                <input
                                    style={{ backgroundColor: 'var(--colorSubtitleSub)' }}
                                    type="text"
                                    value={lastPieces}
                                    onChange={e => { handleLastPiecesChange(e); if (fieldErrors.feedbackRange) clearFieldError('feedbackRange'); }}
                                    onBlur={() => { if (selectedFeedbackRange === 'PIECES') validateFeedbackRange('PIECES', lastDays, lastPieces); }}
                                    disabled={selectedFeedbackRange !== 'PIECES'}
                                    aria-label="Enter number of feedback pieces(1-250) for Feedback range"
                                    className="feedbackRangeInput"
                                />{' '}
                                Pieces of Feedback (1-250)
                            </label>
                        </div>
                    </div>
                    <div id="feedbackRangeError" aria-live="polite" style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                        {fieldErrors.feedbackRange}
                    </div>
                </section>
                )}
                <section style={{ display: 'flex', alignItems: 'baseline', flexWrap:"nowrap",whiteSpace:"nowrap" }}>
                    <div className='publicFeedbackNameHeader'>Refresh Rate</div>
                    <div className='publicRefreshRateContainer'>
                        <TextField value={refreshRate}
                                   onChange={event => { setRefreshRate(event.target.value.trim()); if (fieldErrors.refreshRate) validateRefreshRate(event.target.value.trim()); }}
                                   onBlur={event => validateRefreshRate(event.target.value.trim())}
                                   variant="outlined"
                                   className="publicRefreshRateInput"
                                   inputRef={refreshRateRef}
                                   error={!!fieldErrors.refreshRate}
                                   helperText={fieldErrors.refreshRate || ''}
                                   inputProps={{
                                       'aria-label': 'Refresh rate required in hours',
                                       'aria-describedby': fieldErrors.refreshRate ? 'refreshRateError' : undefined,
                                   }}
                                   FormHelperTextProps={{ id: 'refreshRateError', 'aria-live': 'polite' }}
                        />
                        <div className='publicFeedbackNameHeader'>Hours <span style={{ color: 'red' }}>*</span></div>
                    </div>
                </section>
                <div className="actionBar " style={{background: 'var(--colorBackgroundPrimary)'}}>
                    <div className="actions">
                        <div
                            tabIndex={0}
                            role="button"
                            aria-label="Save"
                            onClick={!saving ? saveFeedbackSetting : null}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !saving) saveFeedbackSetting(); }}
                            className={`button medium ${!saving ? 'confirm' : 'dismiss disabled'}`}
                        >
                            <div className="title" aria-hidden="true">Save</div>
                        </div>

                        <div
                            tabIndex={0}
                            role="button"
                            aria-label="Cancel"
                            onClick={onCancel}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onCancel();
                                else if (e.key === 'Tab' && !e.shiftKey && props.onCancelTab) { e.preventDefault(); props.onCancelTab(); }
                            }}
                            className="button dismiss medium"
                        >
                            <div className="title" aria-hidden="true">Cancel</div>
                        </div>
                    </div>
                </div>
            </> : <CircularProgress/>}
        </div>
    )
}

export default CreateEditPublicFeedbackBoard;
